import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, X, User } from "lucide-react";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { useGlobalSearch, type GlobalSearchResult } from "@/hooks/useGlobalSearch";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";

interface GlobalSearchProps {
  onClose?: () => void;
}

export function GlobalSearch({ onClose }: GlobalSearchProps) {
  const { t } = useTranslation("nav");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<'all' | 'pet' | 'owner'>('all');

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [query]);

  const { data: results = [], isFetching } = useGlobalSearch(debouncedQuery);

  const filteredResults = useMemo(() => {
    if (activeFilter === 'all') return results;
    return results.filter(item => item.type === activeFilter);
  }, [results, activeFilter]);

  const groupedResults = useMemo(
    () =>
      filteredResults.reduce(
        (acc, item) => {
          acc[item.type].push(item);
          return acc;
        },
        { pet: [] as GlobalSearchResult[], owner: [] as GlobalSearchResult[] },
      ),
    [filteredResults],
  );

  const trimmedQuery = query.trim();
  const isDebouncing = trimmedQuery !== debouncedQuery;
  const isLoading = isFetching || (trimmedQuery.length > 0 && isDebouncing);

  const petCount = results.filter((result) => result.type === "pet").length;
  const ownerCount = results.filter((result) => result.type === "owner").length;

  const handleCloseAndReset = () => {
    setQuery("");
    setDebouncedQuery("");
    setActiveFilter('all');
    onClose?.();
  };

  const handleClearSearch = () => {
    setQuery("");
    setDebouncedQuery("");
    setActiveFilter('all');
  };

  return (
    <div className="flex flex-col min-h-0">
      {/* Header with search input */}
      <div className="p-4 md:p-6 border-b">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="flex flex-1 items-center gap-3 rounded-xl border border-border bg-card px-4 py-2 shadow-sm transition-all duration-200 focus-within:border-primary focus-within:shadow-md focus-within:ring-2 focus-within:ring-primary/20">
            <Search className="h-4 w-4 text-muted-foreground transition-colors" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("globalSearch.placeholder")}
              className="rounded-none h-auto flex-1 border-none bg-transparent p-0 text-sm placeholder:text-muted-foreground focus-visible:ring-0"
              autoFocus
            />
            {query && (
              <Button
                variant="ghost"
                size="icon"
                className="h-4 md:h-6 w-4 md:w-6 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={handleClearSearch}
              >
                <X className="h-3.5 w-3.5" />
                <span className="sr-only">{t("common:clearSearch")}</span>
              </Button>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 md:h-10 md:w-10 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={handleCloseAndReset}
            aria-label={t("common:close")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filter buttons */}
      {trimmedQuery.length >= 1 && !isLoading && results.length > 0 && (
        <div className=" mx-auto md:mx-0 md:px-6 pt-4 pb-[0.6rem]">
          <div className="flex gap-2">
            <Button
              variant={activeFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter('all')}
              className="transition-all rounded-full"
            >
              {t("globalSearch.filters.all", { count: results.length })}
            </Button>
            <Button
              variant={activeFilter === 'pet' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter('pet')}
              className="transition-all rounded-full"
            >
              {t("globalSearch.filters.pet", { count: petCount })}
            </Button>
            <Button
              variant={activeFilter === 'owner' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter('owner')}
              className="transition-all rounded-full"
            >
              {t("globalSearch.filters.owner", { count: ownerCount })}
            </Button>
          </div>
        </div>
      )}

      {/* Results section - scrollable */}
      {trimmedQuery.length >= 1 && (
        <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
          {isLoading ? (
            <div className="flex items-center gap-3 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <div>
                <p className="font-medium">{t("globalSearch.searchingTitle")}</p>
                <p className="text-xs">{t("globalSearch.searchingDescription", { query: trimmedQuery })}</p>
              </div>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="flex items-center gap-3 py-8 text-sm text-muted-foreground">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                <Search className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium">{t("globalSearch.emptyTitle")}</p>
                <p className="text-xs">
                  {activeFilter === 'all'
                    ? t("globalSearch.emptyDescriptionAll", { query: trimmedQuery })
                    : t("globalSearch.emptyDescriptionFiltered", {
                      filter: t(`globalSearch.filterLabels.${activeFilter}`),
                      query: trimmedQuery,
                    })
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-4">
              {/* Pets Section */}
              {groupedResults.pet.length > 0 && (
                <div>
                  <div className="sticky top-0 bg-background px-4 py-2 -mx-4">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-primary"></div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t("globalSearch.petsSection", { count: groupedResults.pet.length })}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1 mt-2">
                    {groupedResults.pet.map((item) => (
                      <Link
                        key={`${item.type}-${item.id}`}
                        to={item.route}
                        className="group flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50"
                        onClick={handleCloseAndReset}
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary overflow-hidden flex-shrink-0">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="h-full w-full object-cover rounded-full"
                            />
                          ) : (
                            <CustomIcons name="paws" className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-foreground group-hover:text-primary">
                            {item.title}
                            {item.meta?.toLowerCase().includes("falecido") || item.meta?.toLowerCase().includes("deceased") ? (
                              <Badge variant="outline" className="ml-2">
                                {t("globalSearch.deceasedBadge")}
                              </Badge>
                            ) : null}
                          </p>
                          {item.subtitle && (
                            <p className="truncate text-xs text-muted-foreground">
                              {t("globalSearch.ownerPrefix", { value: item.subtitle })}
                            </p>
                          )}
                          {item.meta && (
                            <p className="truncate text-xs text-muted-foreground/80">
                              {item.meta}
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Owners Section */}
              {groupedResults.owner.length > 0 && (
                <div>
                  <div className="sticky top-0 bg-background px-4 py-2 -mx-4">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-amber-500"></div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t("globalSearch.ownersSection", { count: groupedResults.owner.length })}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1 mt-2">
                    {groupedResults.owner.map((item) => (
                      <Link
                        key={`${item.type}-${item.id}`}
                        to={item.route}
                        className="group flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50"
                        onClick={handleCloseAndReset}
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 flex-shrink-0">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-foreground group-hover:text-primary">
                            {item.title}
                          </p>
                          {item.subtitle && (
                            <p className="truncate text-xs text-muted-foreground">
                              {t("globalSearch.documentPrefix", { value: item.subtitle })}
                            </p>
                          )}
                          {item.meta && (
                            <p className="truncate text-xs text-muted-foreground/80">
                              {item.meta}
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
